import { ExternalLink, LayoutDashboard, Send, SquarePen, ThumbsDown, Users, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Link, useParams } from "react-router-dom";

import LayoutAction from "../../actions/LayoutAction";
import { classNames } from "../../AppNav";
import DefaultAvatar from "../../components/DefaultAvatar";
import { Button } from "../../components/ui";
import {
  type GtmLeadCandidate,
  type GtmLeadSearch,
  type GtmPlayStatus,
} from "../api/GtmApi";
import GtmEmptyState from "../components/GtmEmptyState";
import GtmSessionBanner from "../components/GtmSessionBanner";
import { GTM_FIND_LEADS_OVERVIEW } from "../disclosureCopy";
import { useGtmBrainQuery } from "../hooks/useGtmBrain";
import {
  useGtmCandidateDismissMutation,
  useGtmCandidateEnrichMutation,
  useGtmCandidatesQuery,
  useGtmFindLeadsMutation,
  useGtmLeadCompleteReviewMutation,
  useGtmLeadEnrichMutation,
  useGtmLeadSearchQuery,
  useGtmLeadsQuery,
  useGtmLeadStopMutation,
} from "../hooks/useGtmPlayLeads";
import { useGtmPlayPatchMutation, useGtmPlayQuery } from "../hooks/useGtmPlays";
import { useGtmSessionQuery } from "../hooks/useGtmSession";
import LeadSequenceMessagesPanel, {
  sequenceStatusLabel,
  sequenceStatusPillClass,
} from "../sequence/LeadSequenceMessagesPanel";
import PlaySequencePanel from "../sequence/PlaySequencePanel";
import StartContactingModal, {
  type StartContactingPerson,
} from "../sequence/StartContactingModal";

type PlayTab = "overview" | "leads" | "sequence";
type LeadsSubTab = "to_review" | "in_sequence" | "needs_you" | "dismissed";

function statusPillClass(status: GtmPlayStatus) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";
    case "paused":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function searchBanner(search: GtmLeadSearch | null, projectId: string, t: (s: string) => string) {
  if (!search) return null;
  if (search.status === "running") {
    return (
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
        {t("Finding leads…")}
      </div>
    );
  }
  if (search.status === "error") {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900">
        <span className="font-medium">{t("Lead search failed.")}</span>{" "}
        {search.errorMessage || t("Check your LinkedIn session.")}{" "}
        <Link
          className="font-medium underline"
          to={`/workspaces/${projectId}/gtm/leads/settings?key=Leads`}
        >
          {t("Open Settings")}
        </Link>
      </div>
    );
  }
  if (search.status === "ready") {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
        {t("Last search ready")}
        {search.queryText ? (
          <span className="text-green-800/80">
            {" "}
            · {t("Query")}: {search.queryText}
          </span>
        ) : null}
      </div>
    );
  }
  return null;
}

export default function GtmPlayDetailPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId = "", playId = "" } = useParams<{ projectId: string; playId: string }>();
  const [tab, setTab] = useState<PlayTab>("overview");
  const [leadsSubTab, setLeadsSubTab] = useState<LeadsSubTab>("to_review");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [enrichBusyId, setEnrichBusyId] = useState<string | null>(null);

  const [selectedCandidateIds, setSelectedCandidateIds] = useState<string[]>([]);
  const [selectedInSequenceIds, setSelectedInSequenceIds] = useState<string[]>([]);
  const [contactPeople, setContactPeople] = useState<StartContactingPerson[]>([]);
  const [manageMessagesLeadId, setManageMessagesLeadId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const playQuery = useGtmPlayQuery(projectId, playId);
  const searchQuery = useGtmLeadSearchQuery(projectId, playId);
  const candidatesQuery = useGtmCandidatesQuery(projectId, playId);
  const dismissedQuery = useGtmCandidatesQuery(projectId, playId, "dismissed");
  const inSequenceQuery = useGtmLeadsQuery(projectId, playId, "in_sequence_tab");
  const needsYouQuery = useGtmLeadsQuery(projectId, playId, "needs_you");
  const brainQuery = useGtmBrainQuery(projectId);
  const sessionQuery = useGtmSessionQuery(projectId);

  const patchPlayMutation = useGtmPlayPatchMutation(projectId, playId);
  const findLeadsMutation = useGtmFindLeadsMutation(projectId, playId);
  const dismissCandidateMutation = useGtmCandidateDismissMutation(projectId, playId);
  const enrichCandidateMutation = useGtmCandidateEnrichMutation(projectId, playId);
  const enrichLeadMutation = useGtmLeadEnrichMutation(projectId, playId);
  const completeReviewMutation = useGtmLeadCompleteReviewMutation(projectId, playId);
  const stopLeadMutation = useGtmLeadStopMutation(projectId, playId);

  const play = playQuery.data ?? null;
  const search = searchQuery.data ?? null;
  const candidates = candidatesQuery.data || [];
  const inSequenceLeads = inSequenceQuery.data || [];
  const needsYouLeads = needsYouQuery.data || [];
  const dismissedCandidates = dismissedQuery.data || [];
  const brainCalendarUrl = brainQuery.data?.brain?.calendarUrl || "";
  const session = sessionQuery.data ?? null;

  const loading =
    playQuery.isLoading || searchQuery.isLoading || candidatesQuery.isLoading;
  const finding = findLeadsMutation.isPending;
  const updating = patchPlayMutation.isPending;

  useEffect(() => {
    if (play?.name) {
      document.title = `GTM · ${play.name}`;
    } else {
      document.title = "GTM · Play";
    }
  }, [play?.name]);

  useEffect(() => {
    if (!playQuery.isError) return;
    const err: any = playQuery.error;
    dispatch(
      LayoutAction.setNotification({
        title: t("Play"),
        type: "error",
        message: err?.response?.data?.message || err?.message || t("Failed to load play"),
      }) as any
    );
  }, [dispatch, playQuery.error, playQuery.isError, t]);

  const setStatus = async (status: GtmPlayStatus) => {
    if (!play) return;
    try {
      await patchPlayMutation.mutateAsync({ status });
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Play"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Failed to update status"),
        }) as any
      );
    }
  };

  const onFindLeads = async () => {
    try {
      await findLeadsMutation.mutateAsync();
      dispatch(
        LayoutAction.setNotification({
          title: t("Find leads"),
          type: "success",
          message: t("Search started"),
        }) as any
      );
      setTab("leads");
      setLeadsSubTab("to_review");
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Find leads"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Could not start search"),
        }) as any
      );
    }
  };

  const candidateToPerson = (c: GtmLeadCandidate): StartContactingPerson => ({
    sourceId: c.id,
    kind: "candidate",
    name: c.name || c.profileSlug,
    title: c.title,
    company: c.company,
    location: c.location,
    whyFit: c.whyFit,
    linkedinUrl: c.linkedinUrl,
    avatarUrl: c.avatarUrl,
  });

  const openStartContacting = (people: StartContactingPerson[]) => {
    if (people.length === 0) return;
    setContactPeople(people);
  };

  const closeStartContacting = () => {
    setContactPeople([]);
  };

  const onStartContactingCompleted = (summary: {
    ok: number;
    failed: number;
    sequencePaused: boolean;
  }) => {
    setContactPeople([]);
    setSelectedCandidateIds([]);
    setLeadsSubTab("in_sequence");
    const base = summary.sequencePaused
      ? t("Enrolled. Sequence is paused — resume in Settings before Automation sends outreach.")
      : t("Enrolled. Automation advances when sequence is Running.");
    const message =
      summary.ok > 1
        ? `${t("Contacted")} ${summary.ok}${
            summary.failed ? ` · ${summary.failed} ${t("failed")}` : ""
          }. ${base}`
        : base;
    dispatch(
      LayoutAction.setNotification({
        title: t("In sequence"),
        type: summary.failed && !summary.ok ? "error" : "success",
        message,
        duration: 8000,
      }) as any
    );
  };

  const onEnrichCandidate = async (candidateId: string) => {
    try {
      setEnrichBusyId(candidateId);
      await enrichCandidateMutation.mutateAsync(candidateId);
      dispatch(
        LayoutAction.setNotification({
          title: t("Lead"),
          type: "success",
          message: t("Enriched from LinkedIn profile"),
        }) as any
      );
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Lead"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Enrich failed"),
        }) as any
      );
    } finally {
      setEnrichBusyId(null);
    }
  };

  const onDismiss = async (candidateId: string) => {
    try {
      setBusyId(candidateId);
      await dismissCandidateMutation.mutateAsync(candidateId);
      setSelectedCandidateIds((prev) => prev.filter((id) => id !== candidateId));
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Lead"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Dismiss failed"),
        }) as any
      );
    } finally {
      setBusyId(null);
    }
  };

  const onBulkDismiss = async (ids: string[]) => {
    if (ids.length === 0) return;
    setBulkBusy(true);
    let failed = 0;
    for (const id of ids) {
      try {
        await dismissCandidateMutation.mutateAsync(id);
      } catch {
        failed += 1;
      }
    }
    setSelectedCandidateIds([]);
    setBulkBusy(false);
    if (failed > 0) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Reject"),
          type: "error",
          message: `${failed} ${t("failed to reject")}`,
        }) as any
      );
    }
  };

  const onBulkEnrichCandidates = async (ids: string[]) => {
    if (ids.length === 0) return;
    setBulkBusy(true);
    let failed = 0;
    for (const id of ids) {
      try {
        await enrichCandidateMutation.mutateAsync(id);
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    dispatch(
      LayoutAction.setNotification({
        title: t("Enrich"),
        type: failed ? "error" : "success",
        message: failed
          ? `${ids.length - failed} ${t("enriched")} · ${failed} ${t("failed")}`
          : t("Enriched from LinkedIn profile"),
      }) as any
    );
  };

  const onCompleteReview = async (leadId: string) => {
    try {
      setBusyId(leadId);
      await completeReviewMutation.mutateAsync(leadId);
      setLeadsSubTab("in_sequence");
      dispatch(
        LayoutAction.setNotification({
          title: t("Review complete"),
          type: "success",
          message: t("Waiting 6 hours before the first message. Automation advances when sequence is Running."),
        }) as any
      );
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Review"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Complete review failed"),
        }) as any
      );
    } finally {
      setBusyId(null);
    }
  };

  const onStopSequence = async (leadId: string) => {
    try {
      setBusyId(leadId);
      await stopLeadMutation.mutateAsync(leadId);
      setSelectedInSequenceIds((prev) => prev.filter((id) => id !== leadId));
      dispatch(
        LayoutAction.setNotification({
          title: t("Rejected"),
          type: "success",
          message: t("Removed from sequence — no further advances"),
        }) as any
      );
    } catch (err: any) {
      dispatch(
        LayoutAction.setNotification({
          title: t("Reject"),
          type: "error",
          message: err?.response?.data?.message || err?.message || t("Reject failed"),
        }) as any
      );
    } finally {
      setBusyId(null);
    }
  };

  const onBulkRejectInSequence = async (ids: string[]) => {
    if (ids.length === 0) return;
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await stopLeadMutation.mutateAsync(id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setSelectedInSequenceIds([]);
    setBulkBusy(false);
    dispatch(
      LayoutAction.setNotification({
        title: t("Rejected"),
        type: failed && !ok ? "error" : "success",
        message: failed
          ? `${ok} ${t("rejected")} · ${failed} ${t("failed")}`
          : `${ok} ${t("removed from sequence")}`,
      }) as any
    );
  };

  const onBulkEnrichLeads = async (ids: string[]) => {
    if (ids.length === 0) return;
    setBulkBusy(true);
    let failed = 0;
    for (const id of ids) {
      try {
        await enrichLeadMutation.mutateAsync(id);
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    dispatch(
      LayoutAction.setNotification({
        title: t("Enrich"),
        type: failed ? "error" : "success",
        message: failed
          ? `${ids.length - failed} ${t("enriched")} · ${failed} ${t("failed")}`
          : t("Enriched from LinkedIn profile"),
      }) as any
    );
  };

  const toggleCandidateSelect = (id: string) => {
    setSelectedCandidateIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleCandidateSelectAll = () => {
    if (selectedCandidateIds.length === candidates.length) {
      setSelectedCandidateIds([]);
    } else {
      setSelectedCandidateIds(candidates.map((c) => c.id));
    }
  };

  const toggleInSequenceSelect = (id: string) => {
    setSelectedInSequenceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleInSequenceSelectAll = () => {
    if (selectedInSequenceIds.length === inSequenceLeads.length) {
      setSelectedInSequenceIds([]);
    } else {
      setSelectedInSequenceIds(inSequenceLeads.map((l) => l.id));
    }
  };

  const tabs: { id: PlayTab; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "leads", label: "Leads" },
    { id: "sequence", label: "Sequence" },
  ];

  const leadSubTabs: { id: LeadsSubTab; label: string; count?: number }[] = [
    { id: "to_review", label: "To review", count: candidates.length },
    { id: "in_sequence", label: "In sequence", count: inSequenceLeads.length },
    { id: "needs_you", label: "Needs you", count: needsYouLeads.length },
    { id: "dismissed", label: "Dismissed", count: dismissedCandidates.length },
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-200 px-4 pt-3">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm text-gray-500">
          <Link
            to={`/workspaces/${projectId}/gtm/leads?key=Leads`}
            className="hover:text-blue-600"
          >
            {t("Plays")}
          </Link>
          <span>/</span>
          <span className="font-medium text-gray-900">
            {loading ? "…" : play?.name || playId}
          </span>
          {play ? (
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${statusPillClass(
                play.status
              )}`}
            >
              {play.status}
            </span>
          ) : null}
          {play ? (
            <div className="ml-auto flex gap-2">
              {play.status !== "active" ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={updating}
                  onClick={() => setStatus("active")}
                >
                  {t("Activate")}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={updating}
                  onClick={() => setStatus("paused")}
                >
                  {t("Pause")}
                </Button>
              )}
            </div>
          ) : null}
        </div>
        <nav className="flex gap-1" aria-label={t("Play sections")}>
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={classNames(
                tab === item.id
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900",
                "border-b-2 px-3 py-2 text-sm font-medium"
              )}
            >
              {t(item.label)}
            </button>
          ))}
        </nav>
      </div>

      {tab === "overview" ? (
        <div className="space-y-4 px-4 py-4">
          <GtmSessionBanner projectId={projectId} session={session} showPausedNote={false} />
          {searchBanner(search, projectId, t)}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">{t("Lead sourcing")}</h2>
              <p className="text-sm text-gray-500">{t(GTM_FIND_LEADS_OVERVIEW)}</p>
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              isLoading={finding || search?.status === "running"}
              onClick={onFindLeads}
            >
              {t("Find leads now")}
            </Button>
          </div>
          {play ? (
            <>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-gray-200 px-3 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t("To review")}
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">{candidates.length}</p>
                </div>
                <div className="rounded-lg border border-gray-200 px-3 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t("In sequence")}
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">
                    {inSequenceLeads.length}
                  </p>
                </div>
                <div className="rounded-lg border border-gray-200 px-3 py-3">
                  <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                    {t("Needs you")}
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900">{needsYouLeads.length}</p>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 p-4">
                <h2 className="text-sm font-semibold text-gray-900">{t("Audience")}</h2>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">{play.audience}</p>
                <h2 className="mt-4 text-sm font-semibold text-gray-900">{t("Angle")}</h2>
                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-700">
                  {play.angle || t("No angle yet")}
                </p>
              </div>
            </>
          ) : (
            <GtmEmptyState
              icon={LayoutDashboard}
              title="Play overview"
              description="Loading play details…"
            />
          )}
        </div>
      ) : null}

      {tab === "leads" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex gap-1 overflow-x-auto border-b border-gray-100 px-4">
            {leadSubTabs.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setLeadsSubTab(item.id)}
                className={classNames(
                  leadsSubTab === item.id
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-gray-900",
                  "whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium"
                )}
              >
                {t(item.label)}
                {item.count != null && item.count > 0 ? ` (${item.count})` : ""}
              </button>
            ))}
          </div>
          <div
            className={classNames(
              "min-h-0 flex-1 space-y-3 overflow-auto px-4 py-4",
              (leadsSubTab === "to_review" && selectedCandidateIds.length > 0) ||
                (leadsSubTab === "in_sequence" && selectedInSequenceIds.length > 0)
                ? "pb-24"
                : ""
            )}
          >
            <GtmSessionBanner projectId={projectId} session={session} />
            {searchBanner(search, projectId, t)}

            {leadsSubTab === "to_review" ? (
              candidates.length === 0 ? (
                <GtmEmptyState
                  icon={Users}
                  title="No people to review"
                  description="Run Find leads now from Overview. Add to sequence opens a review before Contact enrolls them — outreach never starts from this list alone."
                />
              ) : (
                <>
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                    {candidates.length} {t("new people found")}
                  </div>
                  <PeopleTable
                    rows={candidates.map((c) => ({
                      id: c.id,
                      name: c.name || c.profileSlug,
                      title: c.title,
                      company: c.company,
                      location: c.location,
                      whyFit: c.whyFit,
                      linkedinUrl: c.linkedinUrl,
                      avatarUrl: c.avatarUrl,
                    }))}
                    selectedIds={selectedCandidateIds}
                    onToggleSelect={toggleCandidateSelect}
                    onToggleSelectAll={toggleCandidateSelectAll}
                    actions={(row) => (
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={
                            busyId === row.id ||
                            enrichBusyId === row.id ||
                            bulkBusy ||
                            contactPeople.length > 0
                          }
                          isLoading={enrichBusyId === row.id}
                          onClick={() => onEnrichCandidate(row.id)}
                        >
                          {t("Enrich")}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={
                            busyId === row.id ||
                            enrichBusyId === row.id ||
                            bulkBusy ||
                            contactPeople.length > 0
                          }
                          leftIcon={<Send className="h-3.5 w-3.5" />}
                          onClick={() => {
                            const candidate = candidates.find((c) => c.id === row.id);
                            if (candidate) openStartContacting([candidateToPerson(candidate)]);
                          }}
                        >
                          {t("Add to sequence")}
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={
                            busyId === row.id ||
                            enrichBusyId === row.id ||
                            bulkBusy ||
                            contactPeople.length > 0
                          }
                          title={t("Reject")}
                          onClick={() => onDismiss(row.id)}
                        >
                          <ThumbsDown className="h-3.5 w-3.5" aria-hidden />
                          <span className="sr-only">{t("Reject")}</span>
                        </Button>
                      </>
                    )}
                    t={t}
                  />
                </>
              )
            ) : null}

            {leadsSubTab === "in_sequence" ? (
              inSequenceLeads.length === 0 ? (
                <GtmEmptyState
                  icon={Users}
                  title="No people in sequence"
                  description="People you Contact from To review and those waiting after connection review appear here."
                />
              ) : (
                <PeopleTable
                  rows={inSequenceLeads.map((l) => ({
                    id: l.id,
                    name: l.name || l.profileSlug,
                    title: l.title,
                    company: l.company,
                    location: l.location,
                    whyFit: l.whyFit,
                    linkedinUrl: l.linkedinUrl,
                    avatarUrl: l.avatarUrl,
                    contactId: l.contactId || undefined,
                    status: l.status,
                  }))}
                  selectedIds={selectedInSequenceIds}
                  onToggleSelect={toggleInSequenceSelect}
                  onToggleSelectAll={toggleInSequenceSelectAll}
                  actions={(row) => (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      leftIcon={<SquarePen className="h-3.5 w-3.5" />}
                      onClick={() => setManageMessagesLeadId(row.id)}
                    >
                      {t("Manage messages")}
                    </Button>
                  )}
                  t={t}
                  showStatus
                  statusAsSequencePill
                />
              )
            ) : null}

            {leadsSubTab === "needs_you" ? (
              needsYouLeads.length === 0 ? (
                <GtmEmptyState
                  icon={Users}
                  title="Nothing needs you"
                  description="When a connection is accepted, leads land here for Contact review before the 6-hour wait. Non-positive replies show as hands for you to take over offline. Meeting leads show the calendar link from your brain."
                />
              ) : (
                <PeopleTable
                  rows={needsYouLeads.map((l) => ({
                    id: l.id,
                    name: l.name || l.profileSlug,
                    title: l.title,
                    company: l.company,
                    location: l.location,
                    whyFit: l.whyFit,
                    linkedinUrl: l.linkedinUrl,
                    avatarUrl: l.avatarUrl,
                    email: l.email || undefined,
                    contactId: l.contactId || undefined,
                    status: l.status,
                    calendarUrl:
                      l.calendarUrl ||
                      (l.status === "meeting" ? brainCalendarUrl : undefined) ||
                      undefined,
                  }))}
                  actions={(row) => (
                    <>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        leftIcon={<SquarePen className="h-3.5 w-3.5" />}
                        onClick={() => setManageMessagesLeadId(row.id)}
                      >
                        {t("Manage messages")}
                      </Button>
                      {row.contactId ? (
                        <Link
                          className="text-sm font-medium text-blue-600 hover:underline"
                          to={`/workspaces/${projectId}/items/${row.contactId}?type=CONTACT`}
                        >
                          {t("Open Contact")}
                        </Link>
                      ) : null}
                      {row.status === "review" ? (
                        <Button
                          type="button"
                          variant="primary"
                          size="sm"
                          disabled={busyId === row.id}
                          isLoading={busyId === row.id}
                          onClick={() => onCompleteReview(row.id)}
                        >
                          {t("Complete review")}
                        </Button>
                      ) : null}
                      {row.status === "meeting" && row.calendarUrl ? (
                        <a
                          href={row.calendarUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm font-medium text-blue-600 hover:underline"
                        >
                          {t("Calendar")}
                        </a>
                      ) : null}
                      {row.status === "hands" ? (
                        <span className="text-xs text-gray-500">
                          {t("Reply offline on Contact")}
                        </span>
                      ) : null}
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={busyId === row.id}
                        isLoading={busyId === row.id}
                        onClick={() => onStopSequence(row.id)}
                      >
                        {t("Stop")}
                      </Button>
                    </>
                  )}
                  t={t}
                  showEmail
                  showStatus
                />
              )
            ) : null}

            {leadsSubTab === "dismissed" ? (
              dismissedCandidates.length === 0 ? (
                <GtmEmptyState
                  icon={Users}
                  title="No dismissed people"
                  description="Candidates you dismiss from To review appear here."
                />
              ) : (
                <PeopleTable
                  rows={dismissedCandidates.map((c) => ({
                    id: c.id,
                    name: c.name || c.profileSlug,
                    title: c.title,
                    company: c.company,
                    location: c.location,
                    whyFit: c.whyFit,
                    linkedinUrl: c.linkedinUrl,
                    avatarUrl: c.avatarUrl,
                  }))}
                  actions={() => null}
                  t={t}
                />
              )
            ) : null}
          </div>
        </div>
      ) : null}

      {tab === "sequence" ? (
        <div className="flex-1 overflow-auto p-4">
          <PlaySequencePanel
            projectId={projectId}
            playId={playId}
            onFilterStep={() => {
              setTab("leads");
              setLeadsSubTab("in_sequence");
            }}
            onOpenLead={(leadId) => {
              setTab("leads");
              setLeadsSubTab("in_sequence");
              setManageMessagesLeadId(leadId);
            }}
          />
        </div>
      ) : null}

      {manageMessagesLeadId ? (
        <LeadSequenceMessagesPanel
          projectId={projectId}
          leads={leadsSubTab === "needs_you" ? needsYouLeads : inSequenceLeads}
          selectedLeadId={manageMessagesLeadId}
          onSelectLead={setManageMessagesLeadId}
          onClose={() => setManageMessagesLeadId(null)}
          removing={busyId === manageMessagesLeadId}
          onRemoveFromSequence={async (leadId) => {
            await onStopSequence(leadId);
            setManageMessagesLeadId(null);
          }}
        />
      ) : null}

      {contactPeople.length > 0 ? (
        <StartContactingModal
          projectId={projectId}
          playId={playId}
          people={contactPeople}
          onClose={closeStartContacting}
          onCompleted={onStartContactingCompleted}
        />
      ) : null}

      {leadsSubTab === "to_review" && selectedCandidateIds.length > 0 ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-center px-4">
          <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 shadow-lg">
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="rounded-full"
              disabled={bulkBusy || contactPeople.length > 0}
              leftIcon={<Send className="h-3.5 w-3.5" />}
              onClick={() =>
                openStartContacting(
                  candidates
                    .filter((c) => selectedCandidateIds.includes(c.id))
                    .map(candidateToPerson)
                )
              }
            >
              {`${t("Add")} ${selectedCandidateIds.length} ${t("to sequence")}`}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="rounded-full"
              disabled={bulkBusy || contactPeople.length > 0}
              leftIcon={<ThumbsDown className="h-3.5 w-3.5" />}
              isLoading={bulkBusy}
              onClick={() => onBulkDismiss(selectedCandidateIds)}
            >
              {`${t("Reject")} ${selectedCandidateIds.length}`}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="rounded-full"
              disabled={bulkBusy || contactPeople.length > 0}
              onClick={() => onBulkEnrichCandidates(selectedCandidateIds)}
            >
              {t("Enrich")}
            </Button>
          </div>
        </div>
      ) : null}

      {leadsSubTab === "in_sequence" && selectedInSequenceIds.length > 0 ? (
        <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-center px-4">
          <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 shadow-lg">
            <span className="px-2 text-sm text-gray-600">{t("Already in the sequence")}</span>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="rounded-full"
              disabled={bulkBusy || manageMessagesLeadId != null}
              leftIcon={<ThumbsDown className="h-3.5 w-3.5" />}
              isLoading={bulkBusy}
              onClick={() => onBulkRejectInSequence(selectedInSequenceIds)}
            >
              {`${t("Reject")} ${selectedInSequenceIds.length}`}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="rounded-full"
              disabled={bulkBusy || manageMessagesLeadId != null}
              onClick={() => onBulkEnrichLeads(selectedInSequenceIds)}
            >
              {t("Enrich")}
            </Button>
            <button
              type="button"
              className="rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              aria-label={t("Clear selection")}
              disabled={bulkBusy}
              onClick={() => setSelectedInSequenceIds([])}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

type Row = {
  id: string;
  name: string;
  title?: string;
  company?: string;
  location?: string;
  whyFit?: string;
  linkedinUrl?: string;
  avatarUrl?: string;
  email?: string;
  contactId?: string;
  status?: string;
  calendarUrl?: string;
};

function PersonAvatar({ name, avatarUrl }: { name: string; avatarUrl?: string }) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className="h-8 w-8 shrink-0 rounded-full object-cover bg-gray-100"
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <DefaultAvatar
      name={name}
      type="CONTACT"
      size="sm"
      className="h-8 w-8 shrink-0 rounded-full bg-blue-100 text-blue-800 text-xs font-semibold"
    />
  );
}

function PeopleTable({
  rows,
  actions,
  t,
  showEmail,
  showStatus,
  statusAsSequencePill,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
}: {
  rows: Row[];
  actions: (row: Row) => ReactNode;
  t: (s: string) => string;
  showEmail?: boolean;
  showStatus?: boolean;
  statusAsSequencePill?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (id: string) => void;
  onToggleSelectAll?: () => void;
}) {
  const selectable = Boolean(onToggleSelect && onToggleSelectAll && selectedIds);
  const allSelected = selectable && rows.length > 0 && selectedIds!.length === rows.length;
  const someSelected = selectable && selectedIds!.length > 0 && !allSelected;

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            {selectable ? (
              <th className="w-10 px-3 py-2">
                <input
                  type="checkbox"
                  aria-label={t("Select all")}
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={onToggleSelectAll}
                />
              </th>
            ) : null}
            <th className="px-3 py-2">{t("Name")}</th>
            <th className="px-3 py-2">{t("Title")}</th>
            <th className="px-3 py-2">{t("Company")}</th>
            <th className="px-3 py-2">{t("Location")}</th>
            <th className="min-w-[12rem] px-3 py-2">{t("Why they fit")}</th>
            {showEmail ? <th className="px-3 py-2">{t("Email")}</th> : null}
            {showStatus ? (
              <th className="px-3 py-2">
                {statusAsSequencePill ? t("Sequence status") : t("Status")}
              </th>
            ) : null}
            <th className="px-3 py-2">{t("LinkedIn")}</th>
            <th className="px-3 py-2">{t("Actions")}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row) => (
            <tr key={row.id}>
              {selectable ? (
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    aria-label={`${t("Select")} ${row.name}`}
                    checked={selectedIds!.includes(row.id)}
                    onChange={() => onToggleSelect!(row.id)}
                  />
                </td>
              ) : null}
              <td className="px-3 py-2">
                <div className="flex items-center gap-2.5">
                  <PersonAvatar name={row.name} avatarUrl={row.avatarUrl} />
                  <span className="font-medium text-gray-900">{row.name}</span>
                </div>
              </td>
              <td className="px-3 py-2 text-gray-700">{row.title || "—"}</td>
              <td className="px-3 py-2 text-gray-700">{row.company || "—"}</td>
              <td className="px-3 py-2 text-gray-700">{row.location || "—"}</td>
              <td className="max-w-xs px-3 py-2 text-gray-700">
                <span className="line-clamp-3" title={row.whyFit || undefined}>
                  {row.whyFit || "—"}
                </span>
              </td>
              {showEmail ? (
                <td className="px-3 py-2 text-gray-700">{row.email || "—"}</td>
              ) : null}
              {showStatus ? (
                <td className="px-3 py-2">
                  {row.status ? (
                    statusAsSequencePill ? (
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${sequenceStatusPillClass(
                          row.status
                        )}`}
                      >
                        {sequenceStatusLabel(row.status, t)}
                      </span>
                    ) : (
                      <span className="capitalize text-gray-700">{row.status}</span>
                    )
                  ) : (
                    "—"
                  )}
                </td>
              ) : null}
              <td className="px-3 py-2">
                {row.linkedinUrl ? (
                  <a
                    href={row.linkedinUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    {t("Profile")}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  "—"
                )}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">{actions(row)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
